import React from 'react';

/**
 * How a saved answer reads when the viewer may not change it.
 *
 * A disabled input is a control that does nothing — the honest form is the
 * value itself, and an explicit line when nothing was ever set, rather than an
 * empty box that looks like it is waiting for typing.
 */
export interface ReadOnlyValueProps {
  value?: string | null;
  /** What to say when there is no value — never a blank space. */
  emptyLabel?: string;
  className?: string;
}

export const ReadOnlyValue: React.FC<ReadOnlyValueProps> = ({
  value,
  emptyLabel = 'Not set',
  className = '',
}) =>
  value ? (
    <p className={`m-0 whitespace-pre-wrap text-body-md text-ink ${className}`}>{value}</p>
  ) : (
    <p className={`m-0 text-body-md italic text-ink-muted ${className}`}>{emptyLabel}</p>
  );

/**
 * One read-only chip: a saved value, optionally behind a muted label
 * ("Deliver to  Pune"). The neutral twin of `Badge tone="robbie"` — same
 * height, so the two sit on one row.
 *
 * `truncate` keeps a long value (free text) to one line with an ellipsis —
 * pass the full text as `title`. Below the phone breakpoint it wraps instead:
 * a touch screen has no hover to read the rest.
 */
export interface ReadOnlyChipProps {
  label?: string;
  title?: string;
  truncate?: boolean;
  children: React.ReactNode;
  className?: string;
}

export const ReadOnlyChip: React.FC<ReadOnlyChipProps> = ({ label, title, truncate, children, className = '' }) => (
  <span
    title={title}
    className={
      `inline-flex h-7 items-center gap-s2 rounded-round border border-line-strong bg-canvas px-s4 text-body-sm font-semibold text-ink-secondary ` +
      `${truncate ? 'min-w-0 max-w-[min(420px,100%)] max-md:h-auto max-md:max-w-full max-md:items-baseline max-md:rounded-control max-md:py-s2' : ''} ${className}`
    }
  >
    {label && <span className="shrink-0 font-medium text-ink-muted">{label}</span>}
    {truncate ? <span className="min-w-0 truncate max-md:whitespace-normal">{children}</span> : children}
  </span>
);

/**
 * The read-only face of a chip group: the chosen values only. Showing the
 * unchosen ones greyed out would advertise choices the viewer cannot make.
 */
export interface ReadOnlyChipsProps {
  values: string[];
  /** Maps a stored value to its display label; identity by default. */
  labelFor?: (value: string) => string;
  emptyLabel?: string;
  className?: string;
}

export const ReadOnlyChips: React.FC<ReadOnlyChipsProps> = ({
  values,
  labelFor = (value) => value,
  emptyLabel = 'None selected',
  className = '',
}) => {
  if (values.length === 0) {
    return <p className={`m-0 text-body-md italic text-ink-muted ${className}`}>{emptyLabel}</p>;
  }
  return (
    <div className={`flex flex-wrap gap-s2 ${className}`}>
      {values.map((value) => (
        <ReadOnlyChip key={value}>{labelFor(value)}</ReadOnlyChip>
      ))}
    </div>
  );
};

export default ReadOnlyValue;
